// src/pages/ProductsPage.jsx
import React, { useEffect } from "react";
import { Filter } from "lucide-react";
import "./ProductsGrid.css";

const ProductsGrid = ({ products, type }) => {
  const [selectedBrands, setSelectedBrands] = React.useState([]);
  const [selectedWeights, setSelectedWeights] = React.useState([]);
  const [showInStock, setShowInStock] = React.useState(false);
  const [sortBy, setSortBy] = React.useState();
  const [showFilters, setShowFilters] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState(""); // NEW
  const [imgErrors, setImgErrors] = React.useState({}); //
  const [currentPage, setCurrentPage] = React.useState(1);
  const pageSize = 12;

  const brands = React.useMemo(() => {
    const brandSet = new Set(products.map((p) => p.brand));
    return Array.from(brandSet).sort();
  }, [products]);

  useEffect(() => {
    setSelectedBrands([]);
    setSelectedWeights([]);
    setShowInStock(false);
    if (type === "balls") {
      setSortBy("newest");
    } else {
      setSortBy("name-asc");
    }
    setSearchTerm("");
    setCurrentPage(1);
  }, [type]);
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedBrands, selectedWeights, showInStock, searchTerm, sortBy]);

  const filteredProducts = React.useMemo(() => {
    let filtered = [...products];

    // Filter out products without price
    filtered = filtered.filter((p) => p.price && p.price.trim() !== "");

    // Search by name/title
    if (searchTerm.trim() !== "") {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((p) => p.name.toLowerCase().includes(term));
    }

    // Filter by brand
    if (selectedBrands.length > 0) {
      filtered = filtered.filter((p) => selectedBrands.includes(p.brand));
    }

    // Filter by stock status
    if (showInStock) {
      filtered = filtered.filter((p) => p.stock_status === "In Stock");
    }

    // Filter by available weights (only for products that have weight_stock)
    if (selectedWeights.length > 0) {
      filtered = filtered.filter((p) => {
        if (!p.stock_status) return false; // e.g. shoes, no weights info
        return selectedWeights.some(
          (weight) => p.weight_stock[weight]?.status === "In Stock",
        );
      });
    }

    // Sort products
    switch (sortBy) {
      case "newest": {
        filtered.sort((a, b) => {
          // New items first
          if (a.new !== b.new) {
            return a.new ? -1 : 1;
          }

          // Then sort by scraped_at descending
          const dateA = a.scraped_at ? new Date(a.scraped_at).getTime() : 0;
          const dateB = b.scraped_at ? new Date(b.scraped_at).getTime() : 0;

          if (dateA !== dateB) {
            return dateB - dateA;
          }

          // Final fallback: name ascending
          return a.name.localeCompare(b.name);
        });
        break;
      }
      case "name-asc":
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case "name-desc":
        filtered.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case "brand-asc":
        filtered.sort((a, b) => a.brand.localeCompare(b.brand));
        break;
      case "price-asc": {
        filtered.sort((a, b) => {
          const priceA = parseFloat(
            (a.special_price || a.price).replace(/[^0-9.]/g, ""),
          );
          const priceB = parseFloat(
            (b.special_price || b.price).replace(/[^0-9.]/g, ""),
          );
          return priceA - priceB;
        });
        break;
      }
      case "price-desc": {
        filtered.sort((a, b) => {
          const priceA = parseFloat(
            (a.special_price || a.price).replace(/[^0-9.]/g, ""),
          );
          const priceB = parseFloat(
            (b.special_price || b.price).replace(/[^0-9.]/g, ""),
          );
          return priceB - priceA;
        });
        break;
      }
      default:
        break;
    }

    return filtered;
  }, [
    products,
    selectedBrands,
    showInStock,
    selectedWeights,
    sortBy,
    searchTerm,
  ]);

  const totalPages = Math.ceil(filteredProducts.length / pageSize);

  const paginatedProducts = React.useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredProducts.slice(start, start + pageSize);
  }, [filteredProducts, currentPage, pageSize]);

  const PaginationControls = () => {
    if (totalPages <= 1) return null;

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: "0.75rem",
          marginTop: "1.5rem",
          marginBottom: "1.5rem",
          flexWrap: "wrap",
        }}
      >
        <button
          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
          disabled={currentPage === 1}
          style={{
            padding: "0.5rem 1rem",
            borderRadius: "999px",
            border: "1px solid #ddd",
            backgroundColor: currentPage === 1 ? "#f3f4f6" : "white",
            cursor: currentPage === 1 ? "default" : "pointer",
          }}
        >
          Prev
        </button>

        <span style={{ fontSize: "0.95rem", color: "#666666" }}>
          Page {currentPage} of {totalPages}
        </span>

        <button
          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          disabled={currentPage === totalPages}
          style={{
            padding: "0.5rem 1rem",
            borderRadius: "999px",
            border: "1px solid #ddd",
            backgroundColor: currentPage === totalPages ? "#f3f4f6" : "white",
            cursor: currentPage === totalPages ? "default" : "pointer",
          }}
        >
          Next
        </button>
      </div>
    );
  };

  const toggleBrand = (brand) => {
    setSelectedBrands((prev) =>
      prev.includes(brand) ? prev.filter((b) => b !== brand) : [...prev, brand],
    );
  };

  const clearFilters = () => {
    setSelectedBrands([]);
    setSelectedWeights([]);
    setShowInStock(false);
  };

  const NoImageIcon = () => (
    <svg
      viewBox="0 0 48 48"
      xmlns="http://www.w3.org/2000/svg"
      style={{ width: "48px", height: "48px", color: "#9ca3af" }}
    >
      <rect
        x="6"
        y="8"
        width="36"
        height="28"
        rx="4"
        ry="4"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
      />
      <circle
        cx="18"
        cy="18"
        r="4"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
      />
      <path
        d="M14 30 L22 24 L28 28 L34 22 L40 28"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line
        x1="8"
        y1="10"
        x2="40"
        y2="34"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );

  const headerMessage = type === "balls" ? "Bowling Balls" : "Bowling Shoes";
  const subHeaderMessage =
    type === "balls"
      ? "Browse our complete inventory of premium bowling balls. Contact us to check current availability."
      : "Explore our selection of high-quality bowling shoes. Contact us for current stock information.";

  return (
    <div
      style={{
        backgroundColor: "#f5f5f5",
        minHeight: "100vh",
        paddingTop: "80px",
      }}
    >
      <div
        style={{
          maxWidth: "1400px",
          margin: "0 auto",
          padding: "2rem 1.5rem",
        }}
      >
        {/* Header */}
        <div style={{ marginBottom: "1.5rem" }}>
          <h2
            style={{
              fontSize: "2.5rem",
              fontWeight: "bold",
              color: "#000000",
              marginBottom: "0.5rem",
            }}
          >
            {headerMessage}
          </h2>
          <p
            style={{
              fontSize: "1.1rem",
              color: "#666666",
              marginBottom: "1rem",
            }}
          >
            {subHeaderMessage}
          </p>

          {/* Search bar at top */}
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={
              type === "balls"
                ? "Search by ball name..."
                : "Search by shoe name..."
            }
            style={{
              width: "100%",
              maxWidth: "400px",
              padding: "0.6rem 0.9rem",
              borderRadius: "999px",
              border: "1px solid #ddd",
              fontSize: "0.95rem",
              outline: "none",
              boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
            }}
          />
        </div>

        {/* Mobile Filter Toggle */}
        <button
          onClick={() => setShowFilters((prev) => !prev)}
          className="products-grid__filter-toggle"
        >
          <Filter size={20} />
          Filters
        </button>

        <div className="products-grid__layout">
          {/* Filters Sidebar */}
          <div
            className={`products-grid__sidebar ${
              showFilters ? "products-grid__sidebar--open" : ""
            }`}
          >
            <div
              style={{
                backgroundColor: "white",
                padding: "1.5rem",
                borderRadius: "12px",
                boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                position: "sticky",
                top: "100px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "1.5rem",
                }}
              >
                <h3
                  style={{
                    fontSize: "1.25rem",
                    fontWeight: "bold",
                    color: "#000000",
                  }}
                >
                  Filters
                </h3>
                {(selectedBrands.length > 0 ||
                  selectedWeights.length > 0 ||
                  showInStock) && (
                  <button
                    onClick={clearFilters}
                    style={{
                      color: "#0891b2",
                      fontSize: "0.9rem",
                      fontWeight: "600",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      textDecoration: "underline",
                    }}
                  >
                    Clear All
                  </button>
                )}
              </div>

              {/* Availability (optional – re‑enable if you want) */}
              {/* <div style={{ marginBottom: "1.5rem" }}>
                <h4
                  style={{
                    fontSize: "1rem",
                    fontWeight: "600",
                    color: "#333333",
                    marginBottom: "0.75rem",
                  }}
                >
                  Availability
                </h4>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={showInStock}
                    onChange={(e) => setShowInStock(e.target.checked)}
                    style={{
                      width: "18px",
                      height: "18px",
                      cursor: "pointer",
                      accentColor: "#0891b2",
                    }}
                  />
                  <span style={{ color: "#333333" }}>In Stock Only</span>
                </label>
              </div> */}

              {/* Brand Filter */}
              <div style={{ marginBottom: "1.5rem" }}>
                <h4
                  style={{
                    fontSize: "1rem",
                    fontWeight: "600",
                    color: "#333333",
                    marginBottom: "0.75rem",
                  }}
                >
                  Brand
                </h4>
                <div
                  style={{
                    maxHeight: "300px",
                    overflowY: "auto",
                  }}
                >
                  {brands.map((brand) => (
                    <label
                      key={brand}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        cursor: "pointer",
                        marginBottom: "0.5rem",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedBrands.includes(brand)}
                        onChange={() => toggleBrand(brand)}
                        style={{
                          width: "18px",
                          height: "18px",
                          cursor: "pointer",
                          accentColor: "#0891b2",
                        }}
                      />
                      <span style={{ color: "#333333", fontSize: "0.95rem" }}>
                        {brand}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Products Grid */}
          <div>
            {/* Sort and Results */}
            <div
              className="products-grid__controls-row"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "1.5rem",
                flexWrap: "wrap",
                gap: "1rem",
              }}
            >
              <p
                className="products-grid__count"
                style={{ color: "#666666", fontSize: "1rem" }}
              >
                {filteredProducts.length}{" "}
                {filteredProducts.length === 1 ? "product" : "products"}
              </p>

              <div className="products-grid__pagination-wrapper">
                <PaginationControls />
              </div>

              <div
                className="products-grid__sort"
                style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
              >
                <label style={{ color: "#666666", fontSize: "0.95rem" }}>
                  Sort by:
                </label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  style={{
                    padding: "0.5rem 1rem",
                    borderRadius: "8px",
                    border: "1px solid #ddd",
                    fontSize: "0.95rem",
                    cursor: "pointer",
                    backgroundColor: "white",
                  }}
                >
                  {type === "balls" && <option value="newest">Newest</option>}
                  <option value="name-asc">Name (A-Z)</option>
                  <option value="name-desc">Name (Z-A)</option>
                  <option value="brand-asc">Brand (A-Z)</option>
                  <option value="price-asc">Price (Low-High)</option>
                  <option value="price-desc">Price (High-Low)</option>
                </select>
              </div>
            </div>

            {/* Products Grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                gap: "1.5rem",
              }}
            >
              {paginatedProducts.map((product) => {
                return (
                  <div
                    key={product.sku}
                    style={{
                      backgroundColor: "white",
                      borderRadius: "12px",
                      overflow: "hidden",
                      boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                      transition: "all 0.3s ease",
                      cursor: "pointer",
                      border: "1px solid #e5e5e5",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-4px)";
                      e.currentTarget.style.boxShadow =
                        "0 8px 30px rgba(8,145,178,0.15)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow =
                        "0 4px 20px rgba(0,0,0,0.06)";
                    }}
                  >
                    {/* Product Image */}

                    <div
                      style={{
                        position: "relative",
                        paddingTop: "100%",
                        backgroundColor: "#f5f5f5",
                      }}
                    >
                      {/* New badge */}
                      {product.new && (
                        <div
                          style={{
                            position: "absolute",
                            top: "0.75rem",
                            right: "0.75rem",
                            zIndex: 2,
                            background:
                              "linear-gradient(45deg, #dc2626, #f97316)",
                            color: "white",
                            fontSize: "0.72rem",
                            fontWeight: "700",
                            padding: "0.3rem 0.65rem",
                            borderRadius: "999px",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
                            letterSpacing: "0.02em",
                          }}
                        >
                          New
                        </div>
                      )}
                      {!imgErrors[product.sku] && product.image_url && (
                        <img
                          src={product.image_url}
                          alt={product.name}
                          onError={() =>
                            setImgErrors((prev) => ({
                              ...prev,
                              [product.sku]: true,
                            }))
                          }
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                            padding: "1rem",
                            mixBlendMode: "multiply",
                          }}
                        />
                      )}

                      {(imgErrors[product.sku] || !product.image_url) && (
                        <div
                          style={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "1rem", // match the img padding
                          }}
                        >
                          <NoImageIcon />
                        </div>
                      )}
                    </div>

                    {/* Product Info */}
                    <div style={{ padding: "1.25rem" }}>
                      <div
                        style={{
                          display: "inline-block",
                          padding: "0.25rem 0.75rem",
                          backgroundColor: "#0891b2",
                          color: "white",
                          fontSize: "0.75rem",
                          fontWeight: "bold",
                          borderRadius: "4px",
                          marginBottom: "0.75rem",
                        }}
                      >
                        {product.brand}
                      </div>

                      <h3
                        style={{
                          fontSize: "1.1rem",
                          fontWeight: "600",
                          color: "#000000",
                          marginBottom: "0.75rem",
                          minHeight: "2.5rem",
                          lineHeight: "1.3",
                        }}
                      >
                        {product.name}
                      </h3>
                      <div>
                        {product.special_price ? (
                          <>
                            <h3
                              style={{
                                textDecoration: "line-through",
                                color: "#999999",
                                fontSize: "0.95rem",
                                marginBottom: "0.25rem",
                              }}
                            >
                              {product.price}
                            </h3>
                            <h3
                              style={{
                                color: "#dc2626",
                                fontWeight: "bold",
                                fontSize: "1.1rem",
                              }}
                            >
                              {product.special_price}
                            </h3>
                          </>
                        ) : (
                          <h3>{product.price}</h3>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* No Results */}
            {filteredProducts.length === 0 && (
              <div
                style={{
                  textAlign: "center",
                  padding: "4rem 2rem",
                  backgroundColor: "white",
                  borderRadius: "12px",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                  marginTop: "2rem",
                }}
              >
                <h3
                  style={{
                    fontSize: "1.5rem",
                    fontWeight: "bold",
                    color: "#333333",
                    marginBottom: "0.5rem",
                  }}
                >
                  No products found
                </h3>
                <p
                  style={{
                    color: "#666666",
                    marginBottom: "1.5rem",
                  }}
                >
                  Try adjusting your filters to see more results.
                </p>
                <button
                  onClick={clearFilters}
                  style={{
                    padding: "0.75rem 1.5rem",
                    background: "linear-gradient(45deg, #0891b2, #22d3ee)",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "1rem",
                    fontWeight: "bold",
                    cursor: "pointer",
                  }}
                >
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductsGrid;
